import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import Button from "@hudson/ui/components/FormFields/Button";
import Username from "@hudson/ui/components/FormFields/Username";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import Success from "@hudson/ui/components/Success";
import useContent from "@lib/use_content";
import sendPasswordReset from "@services/send_password_reset";
import { useState } from "react";

interface LoginProps {
  onPageChange?: (page: string) => void;
}

function SendReset({ onPageChange }: LoginProps) {
  const { data, loading } = useContent([
    "beErrFieldRequired",
    "beUsernameLabel",
    "beSendResetpwBackButton",
    "beSendResetpwContent",
    "beSendResetpwSuccessMsg",
    "beSendResetpwTitle",
    "beRequiredPlaceholder",
  ]);
  const formMethods = useForm({});
  const [errorMessage, setErrorMessage] = useState<string>();
  const [success, setSuccess] = useState<boolean>();
  const [submitting, setSubmitting] = useState(false);

  interface ResetData {
    username: string;
  }

  async function submitForm(data: ResetData) {
    setErrorMessage("");
    setSubmitting(true);

    let { success, errorMessage } = await sendPasswordReset(data.username);

    if (!success && errorMessage) {
      setErrorMessage(errorMessage);
    } else if (success) {
      setSuccess(true);
    }
    setSubmitting(false);
  }

  return (
    <>
      {loading ? (
        <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
      ) : (
        <FormProvider {...formMethods}>
          <form
            className="flex flex-col items-center max-w-xs mx-auto  gap-y-6 sm:gap-x-4"
            id="send-reset"
            onSubmit={(e) => {
              e.preventDefault();
              formMethods.handleSubmit(submitForm)();
            }}
            noValidate
          >
            {onPageChange && (
              <div className="self-start mb-4">
                <Button type="button" onClick={() => onPageChange("loginOrCreate")} asLink>
                  {data?.content.beSendResetpwBackButton}
                </Button>
              </div>
            )}

            <h2 className="flex flex-wrap items-center mb-3 text-3xl font-extrabold md:text-3xl opacity-70">
              {data?.content.beSendResetpwTitle}
            </h2>

            {!success && <p>{data?.content.beSendResetpwContent}</p>}

            {errorMessage && <Error className="mt-2" message={errorMessage} heavyStyle scrollTo />}
            {success && (
              <Success message={data?.content.beSendResetpwSuccessMsg} heavyStyle scrollTo />
            )}

            {!success && (
              <>
                <Username
                  autoFocus
                  errorMessage={data?.content.beErrFieldRequired}
                  fieldName="username"
                  label={data?.content.beUsernameLabel}
                  requiredPlaceholder={data?.content.beRequiredPlaceholder}
                />

                <ContinueButton
                  alignLeft
                  className="mt-2 justify-self-start"
                  disabled={submitting}
                  label={data?.content.beSendResetpwSubmitButton}
                  loading={submitting}
                />
              </>
            )}
          </form>
        </FormProvider>
      )}
    </>
  );
}

export default SendReset;
